<template>
  <component :is="wrapperIs" v-bind="wrapperBind">
    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      :label-width="embedded ? '108px' : '120px'"
      size="medium"
      :class="['product-form', { 'form-page-form': !embedded }]"
    >
      <el-row v-if="embedded" :gutter="16">
        <el-col :span="12">
          <el-form-item :label="$t('productFormPage.productName')" prop="productName">
            <el-input v-model="form.productName" :placeholder="$t('productFormPage.productNamePh')" clearable />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item :label="$t('productFormPage.productCode')">
            <el-input v-model="form.productCode" :placeholder="$t('productFormPage.productCodePh')" clearable />
          </el-form-item>
        </el-col>
      </el-row>
      <template v-else>
        <el-form-item :label="$t('productFormPage.productName')" prop="productName">
          <el-input v-model="form.productName" :placeholder="$t('productFormPage.productNamePh')" clearable />
        </el-form-item>
        <el-form-item :label="$t('productFormPage.productCode')">
          <el-input v-model="form.productCode" :placeholder="$t('productFormPage.productCodePh')" clearable />
        </el-form-item>
      </template>

      <el-row v-if="embedded" :gutter="16">
        <el-col :span="12">
          <el-form-item :label="$t('productFormPage.unitPrice')" prop="unitPrice">
            <el-input v-model="form.unitPrice" :placeholder="$t('productFormPage.unitPricePh')" clearable />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item :label="$t('common.status')">
            <el-select v-model="form.statusCode" style="width:100%;">
              <el-option :label="$t('enum.productStatus.1')" :value="1" />
              <el-option :label="$t('enum.productStatus.2')" :value="2" />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <template v-else>
        <el-form-item :label="$t('productFormPage.unitPrice')" prop="unitPrice">
          <el-input v-model="form.unitPrice" :placeholder="$t('productFormPage.unitPricePh')" clearable />
        </el-form-item>
        <el-form-item :label="$t('common.status')">
          <el-select v-model="form.statusCode" style="width:100%;max-width:420px;">
            <el-option :label="$t('enum.productStatus.1')" :value="1" />
            <el-option :label="$t('enum.productStatus.2')" :value="2" />
          </el-select>
        </el-form-item>
      </template>

      <el-form-item :label="$t('productFormPage.description')">
        <el-input v-model="form.description" type="textarea" :rows="embedded ? 2 : 3" :placeholder="$t('productFormPage.descriptionPh')" />
      </el-form-item>

      <el-form-item v-if="!embedded" class="form-page-form__actions">
        <el-button type="primary" :loading="saving" @click="submit">{{ $t('common.save') }}</el-button>
        <el-button @click="$router.push('/product/list')">{{ $t('common.backToList') }}</el-button>
      </el-form-item>
    </el-form>
  </component>
</template>

<script>
import { getProduct, saveProduct, updateProduct } from '@/api/product'
import FormPageShell from '@/components/FormPageShell.vue'

export default {
  name: 'ProductForm',
  components: { FormPageShell },
  props: {
    embedded: { type: Boolean, default: false },
    recordId: { type: [String, Number], default: null }
  },
  data() {
    return {
      isEdit: false,
      saving: false,
      form: {
        productName: '',
        productCode: '',
        unitPrice: 0,
        description: '',
        statusCode: 1
      }
    }
  },
  computed: {
    wrapperIs() {
      return this.embedded ? 'div' : 'FormPageShell'
    },
    wrapperBind() {
      if (this.embedded) {
        return { class: 'product-form__embed' }
      }
      return {
        isEdit: this.isEdit,
        title: this.isEdit ? this.$t('productFormPage.editTitle') : this.$t('productFormPage.newTitle'),
        subtitle: this.isEdit ? '' : this.$t('productFormPage.newSubtitle'),
        icon: 'el-icon-goods',
        theme: 'primary'
      }
    },
    rules() {
      return {
        productName: [{ required: true, message: this.$t('productFormPage.ruleName'), trigger: 'blur' }],
        unitPrice: [{ validator: this.validateUnitPrice, trigger: 'blur' }]
      }
    }
  },
  async created() {
    if (this.embedded) {
      const rid = this.recordId
      this.isEdit = rid != null && rid !== ''
      if (this.isEdit) {
        try {
          await this.loadDetail(rid)
        } catch (e) {
          this.$emit('cancel')
        }
      }
      return
    }
    const id = this.$route.params.id
    if (id) {
      this.isEdit = true
      await this.loadDetail(id)
    }
  },
  methods: {
    validateUnitPrice(rule, value, callback) {
      const n = Number(value)
      if (!Number.isFinite(n) || n < 0) {
        callback(new Error(this.$t('productFormPage.ruleUnitPrice')))
        return
      }
      callback()
    },
    editId() {
      if (this.embedded) return this.recordId
      return this.$route.params.id
    },
    async loadDetail(id) {
      try {
        const r = await getProduct(id)
        const row = (r && r.data) || r
        if (!row || !row.id) {
          this.$message.error(this.$t('productFormPage.notFound'))
          if (!this.embedded) this.$router.replace('/product/list')
          throw new Error('not found')
        }
        this.form = {
          productName: row.productName || '',
          productCode: row.productCode || '',
          unitPrice: row.unitPrice != null ? row.unitPrice : 0,
          description: row.description || '',
          statusCode: row.statusCode != null ? Number(row.statusCode) : 1
        }
      } catch (e) {
        if (e.message !== 'not found') {
          this.$message.error((e && e.message) || this.$t('common.loadFailed'))
          if (!this.embedded) this.$router.replace('/product/list')
        }
        throw e
      }
    },
    buildPayload() {
      return {
        ...this.form,
        unitPrice: Number(this.form.unitPrice) || 0
      }
    },
    submit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return
        this.saving = true
        this.$emit('saving', true)
        try {
          if (this.isEdit) {
            await updateProduct(this.editId(), this.buildPayload())
            this.$message.success(this.$t('productFormPage.saved'))
          } else {
            const resp = await saveProduct(this.buildPayload())
            const raw = resp && resp.data !== undefined ? resp.data : resp
            const newId = raw != null ? Number(raw) : NaN
            this.$message.success(this.$t('productFormPage.created'))
            if (this.embedded) {
              this.$emit('saved', { id: Number.isFinite(newId) ? newId : null })
              return
            }
          }
          if (this.embedded) {
            this.$emit('saved', {})
          } else {
            this.$router.push('/product/list')
          }
        } catch (e) {
          this.$message.error((e && e.message) || this.$t('productFormPage.failed'))
        } finally {
          this.saving = false
          this.$emit('saving', false)
        }
      })
    }
  }
}
</script>

<style scoped>
.form-page-form {
  max-width: 100%;
}

.product-form__embed {
  min-width: 0;
}

.form-page-form__actions {
  margin-top: 8px;
  margin-bottom: 0;
}
</style>
